import { useRequest } from 'ahooks'
import { getVisitCharts } from '@/api'
import React, { useState } from 'react'
import type { TimeRangePickerProps } from 'antd'
import { Button, DatePicker, Flex, Space, Spin, Tag, App } from 'antd'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import Chart from './chart'
import styles from './style.module.scss'

const { RangePicker } = DatePicker

const last90days = dayjs().add(-90, 'd')

const rangePresets: TimeRangePickerProps['presets'] = [
  { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
  { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
  { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
  { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  { label: 'Last 180 Days', value: [dayjs().add(-180, 'd'), dayjs()] },
  {
    label: 'Last 1 Year',
    value: [dayjs().add(-1, 'y'), dayjs()],
  },
  {
    label: 'This Year',
    value: [dayjs().startOf('year'), dayjs()],
  },
]

const sorted = (data: any) =>
  data?.sort((a: any, b: any) => new Date(a.add_date).getTime() - new Date(b.add_date).getTime())

interface SearchComponentProps {
  dateRange: [Dayjs, Dayjs]
  setDateRange: (dateRange: [Dayjs, Dayjs]) => void
  run: () => void
  dateString: string[]
  rangeTotal: { pv: number; uv: number }
}

function SearchComponent({ dateRange, setDateRange, run, dateString, rangeTotal }: SearchComponentProps) {
  return (
    <Flex vertical align='center' justify='center' className={styles.wrapper}>
      <Flex align='center' justify='space-between' className={styles.total}>
        <Space>
          <RangePicker value={dateRange} presets={rangePresets} onChange={setDateRange} allowClear={false} />
          <Button onClick={run}>Refresh</Button>
        </Space>
        <Flex align='center'>
          <span className={styles.label}>
            {dateString[0]} ~ {dateString[1]}:
          </span>
          <Tag color='#097ef9'>PV</Tag>
          <span>{rangeTotal['pv']}</span>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <Tag color='#0dc5c4'>UV</Tag>
          <span>{rangeTotal['uv']}</span>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default function VisitCharts() {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([last90days, dayjs()])
  const dateString = dateRange.map(item => item.format('YYYY-MM-DD'))
  const { message } = App.useApp()

  const { run, data, loading, error } = useRequest(
    () => getVisitCharts({ start_date: dateString[0], end_date: dateString[1] }),
    {
      ready: dateRange,
      debounceWait: 1000,
      debounceLeading: true,
      debounceTrailing: false,
      cacheKey: 'visit-charts',
      onSuccess: () => {
        message.success('Data query successful')
      },
      onError: () => {
        message.error('Data query failed. Please try again later')
      },
    }
  )

  const chartData = sorted(data?.data?.list || [])?.flatMap(item => [
    {
      type: 'pv',
      date: item.add_date,
      value: Number(item.pv),
    },
    {
      type: 'uv',
      date: item.add_date,
      value: Number(item.uv),
    },
  ])
  const rangeTotal = data?.data?.total
  const historyTotal = data?.data?.total_history_all

  if (loading && !data) return <Spin style={{ marginTop: '40vh' }} />

  if (error)
    return (
      <SearchComponent
        dateRange={dateRange}
        setDateRange={setDateRange}
        run={run}
        dateString={dateString}
        rangeTotal={rangeTotal}
      />
    )

  return (
    <Flex vertical align='center' justify='center' className={styles.wrapper}>
      <Flex align='center' justify='flex-end' className={styles.historyTotal}>
        <span className={styles.label}>All of History:</span>
        <Tag color='#097ef9'>PV</Tag>
        <span>{historyTotal['pv']}</span>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <Tag color='#0dc5c4'>UV</Tag>
        <span>{historyTotal['uv']}</span>
      </Flex>
      <SearchComponent
        dateRange={dateRange}
        setDateRange={setDateRange}
        run={run}
        dateString={dateString}
        rangeTotal={rangeTotal}
      />
      <Flex vertical align='center' justify='center' className={styles.chartWrapper}>
        {loading && <Spin className={styles.spin} />}
        <div className={styles.chart}>
          <Chart data={chartData} />
        </div>
      </Flex>
    </Flex>
  )
}
